import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Gift, Building2, Clock, CheckCircle2, AlertTriangle, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { active: "Ativo", used: "Já usado", expired: "Expirado", cancelled: "Cancelado" };

export default function VoucherLanding() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/vouchers/code/${code}`).then((r) => setVoucher(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Voucher não encontrado")).finally(() => setLoading(false));
  }, [code]);

  const claim = async () => {
    setBusy(true);
    try {
      await api.post(`/vouchers/${code}/claim`);
      toast.success("Voucher adicionado à sua carteira!");
      navigate("/vouchers");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-12 text-center text-stone-500">Carregando voucher...</div>;
  if (err) return (
    <div className="max-w-md mx-auto px-4 py-12">
      <Card className="p-6 border-red-300 bg-red-50 rounded-2xl text-center">
        <AlertTriangle className="mx-auto text-red-600 mb-2" size={36}/>
        <h2 className="font-display text-xl font-bold text-red-900">{err}</h2>
      </Card>
    </div>
  );

  const claimed = !!voucher.customer_user_id;
  const mine = user && voucher.customer_user_id === user.id;
  const status = voucher.status;
  const isActive = status === "active";

  return (
    <div className="max-w-md mx-auto px-4 py-8" data-testid="voucher-landing">
      <Card className="p-6 rounded-3xl border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-white text-center">
        <Gift className="mx-auto text-blue-700 mb-2" size={48}/>
        <div className="text-xs uppercase tracking-wider text-blue-900 font-bold">Voucher MorarBem</div>
        <div className="flex items-center justify-center gap-1 text-stone-700 mt-2 text-sm"><Building2 size={14}/>{voucher.company_name}</div>
        <div className="font-display text-5xl font-bold text-stone-900 mt-3">B$ {voucher.value.toFixed(2).replace(".", ",")}</div>
        <div className="mt-4 p-3 rounded-xl bg-white border-2 border-dashed border-stone-300">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Código</div>
          <div className="font-mono text-2xl font-extrabold text-blue-950 tracking-wider mt-1" data-testid="voucher-code">{voucher.code}</div>
        </div>
        <div className="flex items-center justify-center gap-1 text-xs text-stone-500 mt-3"><Clock size={12}/>Válido até {new Date(voucher.expires_at).toLocaleDateString("pt-BR")}</div>

        {!isActive && (
          <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-bold">
            Status: {STATUS_LABEL[status] || status}
          </div>
        )}
      </Card>

      <Card className="mt-4 p-5 rounded-2xl border-stone-200">
        {!user ? (
          <>
            <h3 className="font-display text-lg font-bold text-stone-900 mb-1">Adicione esse voucher à sua carteira</h3>
            <p className="text-sm text-stone-600 mb-4">Crie sua conta MorarBem (rápido e grátis) ou entre para acompanhar seus vouchers, cashback e compras em um só lugar.</p>
            <div className="grid grid-cols-2 gap-2">
              <Link to={`/register?redirect=${encodeURIComponent(`/v/${code}`)}`}>
                <Button className="w-full bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="voucher-register-btn">
                  <UserPlus size={14} className="mr-1"/>Criar conta
                </Button>
              </Link>
              <Link to={`/login?redirect=${encodeURIComponent(`/v/${code}`)}`}>
                <Button variant="outline" className="w-full rounded-xl" data-testid="voucher-login-btn">
                  <LogIn size={14} className="mr-1"/>Entrar
                </Button>
              </Link>
            </div>
          </>
        ) : mine ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto text-emerald-600 mb-1" size={28}/>
            <div className="font-display text-lg font-bold text-stone-900">Este voucher já é seu</div>
            <p className="text-sm text-stone-600 mb-3">Apresente o código no caixa de {voucher.company_name}.</p>
            <Link to="/vouchers"><Button variant="outline" className="rounded-xl">Ver meus vouchers</Button></Link>
          </div>
        ) : claimed ? (
          <div className="text-sm text-red-700 font-bold">Este voucher já foi vinculado a outro cliente.</div>
        ) : (
          <>
            <h3 className="font-display text-lg font-bold text-stone-900 mb-1">Adicione à sua carteira</h3>
            <p className="text-sm text-stone-600 mb-3">Você está logado como <strong>{user.name}</strong>. Adicione este voucher ao seu app.</p>
            <Button onClick={claim} disabled={busy || !isActive} className="w-full bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="voucher-claim-btn">
              {busy ? "Adicionando..." : "Adicionar à minha carteira"}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
